import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Builds the antd `expandable` prop for a schedule of values tree. Keeps every
 * rollup parent expanded as the data refreshes without undoing the rows a user
 * collapsed. Keyed on the joined ids so a fresh array identity from a re-render
 * is not mistaken for a change in the data.
 */
export default function useSovExpandedRowKeys(parentKeys = []) {
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const parentKeysRef = useRef([]);
  const collapsedRowKeysRef = useRef(new Set());

  const parentKeyId = parentKeys.join(',');

  useEffect(() => {
    parentKeysRef.current = parentKeys;
    setExpandedRowKeys(parentKeys.filter((key) => !collapsedRowKeysRef.current.has(key)));
  }, [parentKeyId]);

  const onExpandedRowsChange = useCallback((keys) => {
    const keySet = new Set(keys);
    collapsedRowKeysRef.current = new Set(
      parentKeysRef.current.filter((key) => !keySet.has(key)),
    );
    setExpandedRowKeys(keys);
  }, []);

  return useMemo(() => ({
    childrenColumnName: 'children',
    indentSize: 24,
    // The leading column added by getSovTreeColumns is where the icon belongs.
    expandIconColumnIndex: 0,
    expandedRowKeys,
    onExpandedRowsChange,
  }), [expandedRowKeys, onExpandedRowsChange]);
}
